/* eslint-disable max-len */
import {
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';

import useComunidadesRuraisTradicionaisStore, { IComunidadesRuraisTradicionais } from 'stores/forms/comunidades_rurais_tradicionais';
import { useLocation, useNavigate, NavigateFunction } from 'react-router-dom';
import { ContainerFormFormik } from 'components/shared/container_form_formik';
import ButtonForm from 'components/buttons/button-form';
import { FormikProps } from 'formik';
import { FormularioComunidadesFormik } from 'interfaces/formularioComunidades.interface';
import { useEffect, useRef } from 'react';
import { ROUTES } from 'routes/constants';

interface Step3Props {
  onNext: () => void;
  formik: FormikProps<FormularioComunidadesFormik>;
  open: boolean;
  setOpen: (open: boolean) => void;
  onBack?: () => void;
  stepAtual: number;
  totalSteps: number;
}

type ICRT = IComunidadesRuraisTradicionais;

type LocationState = { backTo?: string };

export const STEP3_KEYS: (keyof IComunidadesRuraisTradicionais)[] = [
  'possui_agua_para_abastecimento_domestico',
  'qual_fonte_da_agua_domestico',
  'possui_agua_para_producao_agropecuaria',
  'qual_fonte_da_producao_agropecuaria',
  'quantas_familia_estao_sem_acesso_agua_domestico',
  'quantas_familia_estao_sem_acesso_agua_producao',
  'comunidade_quilomba_tem_dificil_acesso_agua_domestico',
  'comunidade_quilomba_tem_dificil_acesso_agua_producao',
];

export const boolToYesNo = (v: boolean | null | undefined) => {
  if (v === true) return 'Sim';
  if (v === false) return 'Não';
  return '';
};
export const yesNoToBool = (v: string): boolean | null => {
  if (v === 'Sim') return true;
  if (v === 'Não') return false;
  return null;
};

export const createYesNoRadioHandler = (
  setPartialForm: (k: keyof ICRT, v: unknown) => void,
  key: keyof ICRT,
  resetKey?: keyof ICRT
) => (e: { target: { value: string } }) => {
  const newValue = yesNoToBool(e.target.value);
  setPartialForm(key, newValue as any);
  if (newValue === false && resetKey) setPartialForm(resetKey, null as any);
};

export const createSelectNumberHandler = (
  setPartialForm: (k: keyof ICRT, v: unknown) => void,
  key: keyof ICRT
) => (e: { target: { value: string } }) => {
  setPartialForm(key, Number(e.target.value));
};

export const createNumberInputHandler = (
  setPartialForm: (k: keyof ICRT, v: unknown) => void,
  key: keyof ICRT
) => (e: { target: { value: string } }) => {
  setPartialForm(key, parseInt(e.target.value) || 0);
};

export const validateRequiredField = (field: any, message: string, errors: string[]) => {
  if (field === null || field === undefined || field === '') errors.push(message);
};

export const validateWaterFields = (data: any, errors: string[]) => {
  // Validação de fonte quando responde SIM
  if (data.possui_agua_para_abastecimento_domestico === true) {
    if (data.qual_fonte_da_agua_domestico == null)
      errors.push('Selecione a fonte de água para abastecimento doméstico');
  }
  if (data.possui_agua_para_producao_agropecuaria === true) {
    if (data.qual_fonte_da_producao_agropecuaria == null)
      errors.push('Selecione a fonte de água para produção agropecuária');
  }

  // Validação de quantidade de famílias quando responde NÃO
  if (data.possui_agua_para_abastecimento_domestico === false) {
    if (data.quantas_familia_estao_sem_acesso_agua_domestico == null)
      errors.push('Quantidade de famílias sem água doméstica é obrigatória');
  }
  if (data.possui_agua_para_producao_agropecuaria === false) {
    if (data.quantas_familia_estao_sem_acesso_agua_producao == null)
      errors.push('Quantidade de famílias sem água para produção é obrigatória');
  }
};

export const validateQuilombolaFields = (data: any, errors: string[]) => {
  const isComunidadeQuilombola = data?.tipo_cadastro === 2;
  if (!isComunidadeQuilombola) return;
  validateRequiredField(
    data.comunidade_quilomba_tem_dificil_acesso_agua_domestico,
    'Campo "Dificuldade acesso água doméstica" é obrigatório',
    errors
  );
  validateRequiredField(
    data.comunidade_quilomba_tem_dificil_acesso_agua_producao,
    'Campo "Dificuldade acesso água produção" é obrigatório',
    errors
  );
};



export const validateForm = (d: any) => {
  const errors: string[] = [];

  validateRequiredField(d.ano_referencia_do_questionario, 'Ano de referência é obrigatório', errors);
  validateRequiredField(d.tipo_cadastro, 'Tipo de comunidade é obrigatório', errors);
  if (d.tipo_cadastro === 1 && !d.tipo_de_assentamento) {
    errors.push('Tipo do assentamento é obrigatório quando selecionado "Assentamento"');
  }
  validateRequiredField(d.nome_do_assentamento, 'Nome da Comunidade é obrigatório', errors);
  if (!d.quantidade_de_familias || d.quantidade_de_familias <= 0) {
    errors.push('Quantidade de famílias deve ser maior que zero');
  }

  [
    { field: d.possui_energia_eletrica, msg: 'Campo "Possui energia elétrica" é obrigatório' },
    { field: d.possui_saneamento_basico, msg: 'Campo "Possui abastecimento de água potável" é obrigatório' },
    { field: d.coleta_de_lixo, msg: 'Campo "Possui coleta seletiva de resíduos" é obrigatório' },
    { field: d.recebeu_manutencao_das_estradas_no_ultimo_ano, msg: 'Campo "Manutenção das estradas" é obrigatório' },
    { field: d.possui_escolas, msg: 'Campo "Possui escolas" é obrigatório' },
    { field: d.possui_esgotamento_sanitario, msg: 'Campo "Possui esgotamento sanitário" é obrigatório' },
    { field: d.possui_cultura_manejo_solo, msg: 'Campo "Possui uma cultura de manejo do solo para evitar desertificação" é obrigatório' },
    { field: d.tem_degradacao_solo, msg: 'Campo "É perceptível a degradação do solo" é obrigatório' },
    { field: d.tem_assoreamento_fluvial, msg: 'Campo "É perceptível o assoreamento na rede fluvial" é obrigatório' },
    { field: d.possui_oferta_de_transporte_escolar_para_os_estudantes, msg: 'Campo "Transporte escolar" é obrigatório' },
    { field: d.possui_unidade_de_saude, msg: 'Campo "Possui unidade de saúde" é obrigatório' },
    { field: d.recebeu_agentes_de_saude_do_municipio, msg: 'Campo "Agentes de saúde" é obrigatório' },
    { field: d.tem_acesso_internet, msg: 'Campo "Acesso à internet" é obrigatório' },
  ].forEach(({ field, msg }) => validateRequiredField(field, msg, errors));

  validateWaterFields(d, errors);
  validateQuilombolaFields(d, errors);

  return errors;
};

export const isStep3Pristine = (s: IComunidadesRuraisTradicionais) =>
  STEP3_KEYS.every((k) => s[k] == null || s[k] === '');

export const buildPartialStep3 = (s: IComunidadesRuraisTradicionais): Partial<IComunidadesRuraisTradicionais> => ({
  step_atual: 3,
  possui_agua_para_abastecimento_domestico: s.possui_agua_para_abastecimento_domestico ?? null,
  qual_fonte_da_agua_domestico: s.qual_fonte_da_agua_domestico ?? null,
  possui_agua_para_producao_agropecuaria: s.possui_agua_para_producao_agropecuaria ?? null,
  qual_fonte_da_producao_agropecuaria: s.qual_fonte_da_producao_agropecuaria ?? null,
  quantas_familia_estao_sem_acesso_agua_domestico: s.quantas_familia_estao_sem_acesso_agua_domestico ?? null,
  quantas_familia_estao_sem_acesso_agua_producao: s.quantas_familia_estao_sem_acesso_agua_producao ?? null,
  comunidade_quilomba_tem_dificil_acesso_agua_domestico: s.comunidade_quilomba_tem_dificil_acesso_agua_domestico ?? null,
  comunidade_quilomba_tem_dificil_acesso_agua_producao: s.comunidade_quilomba_tem_dificil_acesso_agua_producao ?? null,
  tem_acesso_internet: s.tem_acesso_internet ?? null,
});

export const buildAddedItem = (s: IComunidadesRuraisTradicionais) => {
  const isQuilombola = s.tipo_cadastro === 2;
  return isQuilombola
    ? {
      nome: s.nome_do_assentamento,
      quantidade_familias: s.quantidade_de_familias,
      possui_energia_eletrica: s.possui_energia_eletrica,
      possui_saneamento_basico: s.possui_saneamento_basico,
      coleta_de_lixo: s.coleta_de_lixo,
      possui_escolas: s.possui_escolas,
      possui_agua_domestico: s.possui_agua_para_abastecimento_domestico,
      possui_agua_agropecuaria: s.possui_agua_para_producao_agropecuaria,
      tem_acesso_internet: s.tem_acesso_internet,
      recebeu_manutencao_das_estradas_no_ultimo_ano: s.recebeu_manutencao_das_estradas_no_ultimo_ano,
    }
    : {
      nome_do_assentamento: s.nome_do_assentamento,
      tipo_de_assentamento: s.tipo_de_assentamento,
      codigo_do_pa: s.codigo_do_pa,
      quantidade_de_familias: s.quantidade_de_familias,
      possui_energia_eletrica: s.possui_energia_eletrica,
      possui_saneamento_basico: s.possui_saneamento_basico,
      coleta_de_lixo: s.coleta_de_lixo,
      possui_escolas: s.possui_escolas,
      possui_agua_para_abastecimento_domestico: s.possui_agua_para_abastecimento_domestico,
      possui_agua_para_producao_agropecuaria: s.possui_agua_para_producao_agropecuaria,
      tem_acesso_internet: s.tem_acesso_internet,
      recebeu_manutencao_das_estradas_no_ultimo_ano: s.recebeu_manutencao_das_estradas_no_ultimo_ano,
    };
};

export const getAddType = (s: IComunidadesRuraisTradicionais) => (s.tipo_cadastro === 2 ? 'comunidades' : 'assentamentos');

export const saveLastAddedToLocalStorage = (pacoteId: string | undefined, addType: string, item: unknown) => {
  try {
    if (!pacoteId) return false;
    localStorage.setItem(`crt:lastAdded:${pacoteId}`, JSON.stringify({ ts: Date.now(), addType, item }));
    return true;
  } catch (e) {
    // swallow to keep runtime behavior unchanged
    // eslint-disable-next-line no-console
    console.warn('[STEP3] falha ao salvar cache otimista', e);
    return false;
  }
};

export const isLocationState = (x: unknown): x is LocationState => {
  if (typeof x !== 'object' || x === null) return false;
  const rec = x as Record<string, unknown>;
  return !('backTo' in rec) || typeof rec.backTo === 'string';
};

export type SubmitStep3Deps = {
  state: IComunidadesRuraisTradicionais;
  setPartialForm: (k: keyof ICRT, v: unknown) => void;
  saveStep3: () => Promise<boolean>;
  location: { state?: unknown };
  navigate: NavigateFunction;
  alertFn?: (m: string) => void;
};

export const submitStep3 = async ({
  state,
  setPartialForm,
  saveStep3,
  location,
  navigate,
  alertFn = (m: string) => window.alert(m),
}: SubmitStep3Deps) => {
  const validationErrors = validateForm(state);
  if (validationErrors.length > 0) {
    alertFn(`Não é possível enviar o formulário. Corrija os erros:\n\n${validationErrors.join('\n')}`);
    return { ok: false, errors: validationErrors };
  }

  const partialStep3 = buildPartialStep3(state);

  (Object.entries(partialStep3) as [
    keyof IComunidadesRuraisTradicionais,
    IComunidadesRuraisTradicionais[keyof IComunidadesRuraisTradicionais] | undefined
  ][]).forEach(([k, v]) => {
    if (v !== undefined) setPartialForm(k, v);
  });

  const ok = await saveStep3();
  if (!ok) {
    alertFn('Erro ao enviar questionário');
    return { ok: false };
  }

  alertFn('Questionário enviado com sucesso');

  const s = { ...state, ...partialStep3 } as IComunidadesRuraisTradicionais;
  const addedItem = buildAddedItem(s as IComunidadesRuraisTradicionais);
  const addType = getAddType(s as IComunidadesRuraisTradicionais);
  const pacoteId = s.pacote_formularios_uuid || s.uuid;

  // attempt optimistic save to localStorage
  saveLastAddedToLocalStorage(pacoteId as string | undefined, addType, addedItem);

  const backTo = isLocationState(location.state) ? (location.state as LocationState).backTo : undefined;
  const destino = backTo ?? ROUTES.PACOTE_FORMULARIOS;

  navigate(destino, {
    replace: true,
    state: { refresh: Date.now(), addType, item: addedItem },
  });

  return { ok: true, addedItem, addType, destino };
};

export const Step3: React.FC<Step3Props> = ({
  onNext,
  onBack,
  stepAtual,
  totalSteps,
}) => {
  const {
    comunidadesRuraisTradicionais,
    setPartialForm,
    saveStep3
  } = useComunidadesRuraisTradicionaisStore();

  const navigate = useNavigate();
  const location = useLocation();
  const locationState = (useLocation().state ?? {}) as { mode?: 'edit' | 'create' };

  const isAssentamento = comunidadesRuraisTradicionais.tipo_cadastro === 1;
  const isComunidadeQuilombola = comunidadesRuraisTradicionais.tipo_cadastro === 2;

  const fontesAgua = [
    { value: 1, label: 'Córrego' },
    { value: 2, label: 'Rio ou outro curso d\'água' },
    { value: 3, label: 'Poço comum' },
    { value: 4, label: 'Poço artesiano ou semi-artesiano' },
    { value: 5, label: 'Captação da chuva' },
    { value: 6, label: 'Água potável de órgão/companhia de abastecimento' },
  ];

  const yearSubtitle = '';
  const aguaAbastecimentoValue = boolToYesNo(comunidadesRuraisTradicionais.possui_agua_para_abastecimento_domestico);
  const aguaProducaoValue = boolToYesNo(comunidadesRuraisTradicionais.possui_agua_para_producao_agropecuaria);
  const didReset = useRef(false);
  useEffect(() => {
    const isEdit = locationState.mode === 'edit';
    if (isEdit || didReset.current) return;

    const pristine = isStep3Pristine(comunidadesRuraisTradicionais);
    if (!pristine) return;

    STEP3_KEYS.forEach((k) => setPartialForm(k, null as any));
    didReset.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationState.mode]);

  const handleAdvanced = async () => {
    await submitStep3({
      state: comunidadesRuraisTradicionais,
      setPartialForm,
      saveStep3,
      location,
      navigate,
      alertFn: (m) => alert(m),
    });
  };

  return (
    <>
      <Typography
        variant='h5'
        gutterBottom
        sx={{ mt: 4, mb: 3, fontSize: '24px', fontWeight: 400, textAlign: 'center' }}
      >
        Serviços Ambientais
      </Typography>

      <Box>
        <ContainerFormFormik
          title='Serviços Ambientais'
          subtitle={yearSubtitle}
          onSubmit={onNext}
        >
          <Grid item xs={12}>
            {/* 14 - Água doméstico */}
            <Typography variant='subtitle1' sx={{ mb: 2, fontWeight: 'bold' }}>
              Possui água para abastecimento doméstico (consumo da família)?
            </Typography>
            <RadioGroup
              row
              value={aguaAbastecimentoValue}
              onChange={createYesNoRadioHandler(setPartialForm, 'possui_agua_para_abastecimento_domestico', 'qual_fonte_da_agua_domestico')}
            >
              <FormControlLabel value="Sim" control={<Radio />} label="Sim" />
              <FormControlLabel value="Não" control={<Radio />} label="Não" />
            </RadioGroup>

            {/* 15 - Fonte água doméstico */}
            {comunidadesRuraisTradicionais.possui_agua_para_abastecimento_domestico === true && (
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12}>
                  <Typography variant='subtitle1' sx={{ mb: 2, fontWeight: 'bold' }}>
                    Qual a fonte?
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      displayEmpty
                      value={comunidadesRuraisTradicionais.qual_fonte_da_agua_domestico?.toString() ?? ''}
                      onChange={createSelectNumberHandler(setPartialForm, 'qual_fonte_da_agua_domestico') as unknown as (e: SelectChangeEvent) => void}
                    >
                      <MenuItem value='' disabled>
                        <em>Selecione a fonte da água</em>
                      </MenuItem>
                      {fontesAgua.map((fonte) => (
                        <MenuItem key={fonte.value} value={fonte.value}>
                          {fonte.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            )}

            {/* 16 - Água produção */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12}>
                <Typography variant='subtitle1' sx={{ mb: 2, fontWeight: 'bold' }}>
                  Possui água para a produção agropecuária?
                </Typography>
                <RadioGroup
                  row
                  value={aguaProducaoValue}
                  onChange={createYesNoRadioHandler(setPartialForm, 'possui_agua_para_producao_agropecuaria', 'qual_fonte_da_producao_agropecuaria')}
                >
                  <FormControlLabel value="Sim" control={<Radio />} label="Sim" />
                  <FormControlLabel value="Não" control={<Radio />} label="Não" />
                </RadioGroup>
              </Grid>
            </Grid>

            {/* 17 - Fonte água produção */}
            {comunidadesRuraisTradicionais.possui_agua_para_producao_agropecuaria === true && (
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12}>
                  <Typography variant='subtitle1' sx={{ mb: 2, fontWeight: 'bold' }}>
                    Qual a fonte?
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      displayEmpty
                      value={comunidadesRuraisTradicionais.qual_fonte_da_producao_agropecuaria?.toString() ?? ''}
                      onChange={createSelectNumberHandler(setPartialForm, 'qual_fonte_da_producao_agropecuaria') as unknown as (e: SelectChangeEvent) => void}
                    >
                      <MenuItem value='' disabled>
                        <em>Selecione a fonte da água</em>
                      </MenuItem>
                      {fontesAgua.map((fonte) => (
                        <MenuItem key={fonte.value} value={fonte.value}>
                          {fonte.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            )}

            {/* Números de famílias sem acesso à água – Assentamentos e Quilombolas */}
            {(isAssentamento || isComunidadeQuilombola) && (
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant='subtitle1' sx={{ mb: 2, fontWeight: 'bold' }}>
                    Quantas famílias estão sem acesso ou com acesso precário à água para abastecimento doméstico?
                  </Typography>
                  <TextField
                    fullWidth
                    type='number'
                    placeholder='Digite o número de famílias'
                    value={comunidadesRuraisTradicionais.quantas_familia_estao_sem_acesso_agua_domestico?.toString() ?? ''}
                    onChange={createNumberInputHandler(setPartialForm, 'quantas_familia_estao_sem_acesso_agua_domestico')}
                    inputProps={{ min: 0 }}
                  />
                </Grid>

                <Grid item xs={6}>
                  <Typography variant='subtitle1' sx={{ mb: 2, fontWeight: 'bold' }}>
                    Quantas famílias estão sem acesso ou com acesso precário à água para produção?
                  </Typography>
                  <TextField
                    fullWidth
                    type='number'
                    placeholder='Digite o número de famílias'
                    value={comunidadesRuraisTradicionais.quantas_familia_estao_sem_acesso_agua_producao?.toString() ?? ''}
                    onChange={createNumberInputHandler(setPartialForm, 'quantas_familia_estao_sem_acesso_agua_producao')}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
              </Grid>
            )}

            {/* Comunidades Quilombolas – dificuldades */}
            {isComunidadeQuilombola && (
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant='subtitle1' sx={{ mb: 2, fontWeight: 'bold' }}>
                    A comunidade quilombola tem dificuldade no acesso à água para abastecimento doméstico?
                  </Typography>
                  <RadioGroup
                    row
                    value={boolToYesNo(comunidadesRuraisTradicionais.comunidade_quilomba_tem_dificil_acesso_agua_domestico)}
                    onChange={createYesNoRadioHandler(setPartialForm, 'comunidade_quilomba_tem_dificil_acesso_agua_domestico')}
                  >
                    <FormControlLabel value="Sim" control={<Radio />} label="Sim" />
                    <FormControlLabel value="Não" control={<Radio />} label="Não" />
                  </RadioGroup>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant='subtitle1' sx={{ mb: 2, fontWeight: 'bold' }}>
                    A comunidade quilombola tem dificuldade no acesso à água para produção?
                  </Typography>
                  <RadioGroup
                    row
                    value={boolToYesNo(comunidadesRuraisTradicionais.comunidade_quilomba_tem_dificil_acesso_agua_producao)}
                    onChange={createYesNoRadioHandler(setPartialForm, 'comunidade_quilomba_tem_dificil_acesso_agua_producao')}
                  >
                    <FormControlLabel value="Sim" control={<Radio />} label="Sim" />
                    <FormControlLabel value="Não" control={<Radio />} label="Não" />
                  </RadioGroup>
                </Grid>
              </Grid>
            )}
          </Grid>
        </ContainerFormFormik>

        <ButtonForm
          disabled={false}
          onBack={onBack}
          onAdvanced={handleAdvanced}
          onSendApproved={handleAdvanced}
          stepAtual={stepAtual}
          totalSteps={totalSteps}
        />
      </Box>
    </>
  );
};
